import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProfilePicture } from '../utils/profilePicture';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Player {
  id: string;
  email?: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  address?: string;
  soccerExperience?: string;
  teams: Array<{
    teamId: string;
    teamName?: string;
    teamNumber?: string;
    role: string;
    position?: string;
    jerseyNumber?: number;
    isGuest: boolean;
    guestMatchId?: string;
    joinedAt: string;
  }>;
  statistics: Array<{
    teamId: string;
    matchesPlayed: number;
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
  }>;
  matchesPlayed: number;
}

const PlayerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const response = await api.get(`/players/${id}`);
        setPlayer(response.data);
      } catch (error) {
        console.error('Error fetching player:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [id]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!player) {
    return <div className="container">Player not found</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
          <img 
            src={getProfilePicture(player)} 
            alt={`${player.firstName} ${player.lastName}`}
            style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover' }}
          />
          <div>
            <h1 style={{ margin: 0 }}>{player.firstName} {player.lastName}</h1>
            {player.email && <p style={{ color: '#757575', margin: '5px 0' }}>{player.email}</p>}
          </div>
        </div>

        <div style={{ marginTop: '30px' }}>
          <h2>Personal Information</h2>
          <p><strong>Date of Birth:</strong> {player.dateOfBirth ? new Date(player.dateOfBirth).toLocaleDateString() : 'N/A'}</p>
          <p><strong>Phone:</strong> {player.phoneNumber || 'N/A'}</p>
          <p><strong>Address:</strong> {player.address || 'N/A'}</p>
          <p><strong>Soccer Experience:</strong> {player.soccerExperience || 'N/A'}</p>
        </div>

        <div style={{ marginTop: '30px' }}>
          <h2>Team Memberships ({player.teams.length})</h2>
          {player.teams.length === 0 ? (
            <p>Not on any teams.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Role</th>
                  <th>Position</th>
                  <th>Jersey #</th>
                  <th>Type</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {player.teams.map((team, idx) => (
                  <tr key={idx}>
                    <td>
                      <Link to={`/teams/${team.teamId}`}>
                        {team.teamName || 'Unknown'} ({team.teamNumber || 'N/A'})
                      </Link>
                    </td>
                    <td>
                      <span className={`badge ${team.role === 'captain' ? 'badge-info' : 'badge-secondary'}`}>
                        {team.role}
                      </span>
                    </td>
                    <td>{team.position || '-'}</td>
                    <td>{team.jerseyNumber || '-'}</td>
                    <td>
                      {team.isGuest ? (
                        <span className="badge badge-warning">Guest</span>
                      ) : (
                        <span className="badge badge-success">Regular</span>
                      )}
                    </td>
                    <td>{new Date(team.joinedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: '30px' }}>
          <h2>Statistics</h2>
          {player.statistics.length === 0 ? (
            <p>No statistics available.</p>
          ) : (
            <>
              {/* Total/Aggregated Statistics */}
              <div className="card" style={{ marginBottom: '30px', backgroundColor: '#F5F5F5' }}>
                <h3 style={{ marginBottom: '15px', color: '#2E7D32' }}>Total Statistics (All Teams)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                  <div>
                    <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>Matches Played</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#2E7D32' }}>
                      {player.statistics.reduce((sum, stat) => sum + stat.matchesPlayed, 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>Goals</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#2E7D32' }}>
                      ⚽ {player.statistics.reduce((sum, stat) => sum + stat.goals, 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>Assists</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#7c3aed' }}>
                      🎯 {player.statistics.reduce((sum, stat) => sum + stat.assists, 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>Yellow Cards</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#FFC107' }}>
                      🟨 {player.statistics.reduce((sum, stat) => sum + stat.yellowCards, 0)}
                    </p>
                  </div>
                  <div>
                    <p style={{ color: '#757575', fontSize: '14px', margin: 0 }}>Red Cards</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#D32F2F' }}>
                      🟥 {player.statistics.reduce((sum, stat) => sum + stat.redCards, 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Statistics by Team */}
              <div>
                <h3 style={{ marginBottom: '15px' }}>Statistics by Team</h3>
                <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                  {player.statistics.map((stat, idx) => {
                    const team = player.teams.find(t => t.teamId === stat.teamId);
                    return (
                      <div key={idx} className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                          <h4 style={{ margin: 0 }}>
                            <Link to={`/teams/${stat.teamId}`} style={{ color: '#2E7D32', textDecoration: 'none' }}>
                              {team?.teamName || 'Unknown Team'}
                            </Link>
                          </h4>
                          {team?.teamNumber && (
                            <span style={{ color: '#757575', fontSize: '12px' }}>{team.teamNumber}</span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Matches</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0' }}>{stat.matchesPlayed}</p>
                          </div>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Goals</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#2E7D32' }}>
                              ⚽ {stat.goals}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Assists</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#7c3aed' }}>
                              🎯 {stat.assists}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Yellow Cards</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#FFC107' }}>
                              🟨 {stat.yellowCards}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Red Cards</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0', color: '#D32F2F' }}>
                              🟥 {stat.redCards}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#757575', fontSize: '12px', margin: 0 }}>Goal Ratio</p>
                            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '5px 0 0 0' }}>
                              {stat.matchesPlayed > 0 ? (stat.goals / stat.matchesPlayed).toFixed(2) : '0.00'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: '30px' }}>
          <Link to="/players" className="btn btn-secondary">Back to Players</Link>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetail;

